import debounce from "lodash.debounce";
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import {
  fetchGet,
  fetchPost,
  toQueryString,
} from "../../../service/api_client";
import AutoCompleteField from "../../../ui/new_components/common/autocomplete";

import { ItemModelV2 } from "../models/item_model";

interface ItemSearchFieldProps {
  onSelect: (data: ItemModelV2) => void;
  disabled?: boolean;
  placeholder?: string;
  value: ItemModelV2;
  /** Filters results by item `id` (not item_code). Sent to backend. */
  item_ids?: string[];
  /** Filters results by item_code. Applied client-side after search results return. */
  item_codes?: string[];
  id?: string;
}

const NewItemSearchByIdField: FunctionComponent<ItemSearchFieldProps> = (
  props,
) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/item_ids`;
  const { showToast } = useToast();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] = useState<ItemModelV2[]>();

  const allowedItemCodesRef = useRef<Set<string> | undefined>(undefined);

  useEffect(() => {
    if (props.item_codes === undefined) {
      allowedItemCodesRef.current = undefined;
      return;
    }

    allowedItemCodesRef.current = new Set(props.item_codes);
  }, [props.item_codes]);

  const getData = async (prop: {
    search?: string;
    page: number;
    count: number;
    all?: boolean;
    item_ids?: string[];
  }) => {
    setLoading(true);
    try {
      const isLargeRequest = prop.item_ids && prop.item_ids.length > 100;
      const requestUrl = isLargeRequest
        ? url // use POST
        : url + toQueryString(prop); // small query, safe GET
      console.log("isLargeRequest:", isLargeRequest);
      const res = isLargeRequest
        ? await fetchPost(requestUrl, prop)
        : await fetchGet(requestUrl);

      if (res.success) {
        const items: ItemModelV2[] = res.data.data ?? [];
        const allowed = allowedItemCodesRef.current;
        if (allowed) {
          setData(items.filter((i) => i.item_code && allowed.has(i.item_code)));
        } else {
          setData(items);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const debouncedHandler = useCallback(
    debounce((query: string, item_ids?: string[]) => {
      const allowed = allowedItemCodesRef.current;
      if (allowed && allowed.size === 0) {
        setData([]);
        return;
      }

      if (query)
        getData({
          search: query,
          page: 1,
          count: 1,
          all: true,
          item_ids,
        });
      else setData(undefined);
    }, 500),
    [],
  );

  useEffect(() => {
    if (search && edit) {
      if (props.value && props.value.name === search) return;
      debouncedHandler(search, props.item_ids);
    }
  }, [search, props.item_ids, props.item_codes]);

  useEffect(() => {
    if (
      props.value &&
      props.value !== undefined &&
      Object.keys(props.value).length > 0
    ) {
      setSearch(`${props.value.name}${` - ${props.value.description}`}`);
    }
  }, [props.value]);

  useEffect(() => {
    if (props.disabled) {
      setEdit(false);
      // console.log("props.vale is:", props.value);
      if (
        props.value &&
        props.value !== undefined &&
        Object.keys(props.value).length > 0
      ) {
        setSearch(
          props.value
            ? `${props.value.name}${
                props.value.description ? ` - ${props.value.description}` : ""
              }`
            : "",
        );
      }
    }
  }, [props.disabled, props.value]);

  return (
    <div className="flex gap-1 items-center w-full">
      {edit ? (
        <AutoCompleteField
          id={props.id}
          disabled={props.disabled}
          onAddNew={() => {}}
          loading={loading}
          value={search}
          placeholder={props.placeholder ?? "Select an item "}
          onSelect={(val) => {
            setEdit(false);
            setSearch(val.value.name);
            props.onSelect(val.value);
          }}
          suggestions={data?.map((d) => ({
            label: (
              <div className="text-xs">
                <b>
                  {d.name} - {d.description ?? ""}{" "}
                </b>
                {d.item_code && (
                  <span className="text-gray-500">
                    (Item Code: {d.item_code})
                  </span>
                )}
              </div>
            ),

            value: d,
          }))}
          onChange={setSearch}
        />
      ) : (
        <div
          id={props.id}
          title={search}
          className={`text-xs px-3 py-2  h-8 rounded focus:outline-none w-full border overflow-hidden overflow-ellipsis ${
            search ? "" : " text-gray-400 "
          }`}
          onClick={() => {
            if (!props.disabled) {
              setEdit(true);
              // setSearch(emp?.name ?? "");
            }
          }}
        >
          {search || props.placeholder || "Select an item "}
        </div>
      )}
    </div>
  );
};

export default NewItemSearchByIdField;
